import React, { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { MaterialReactTable, MRT_ColumnDef } from "material-react-table";
import { Navigate, useParams } from "react-router-dom";
import getTableLang from "src/shared/services/languages/MaterialReactTable/getTableLang";
import useCustomMaterialTable from "src/shared/hooks/useCustomMaterialTable";
import useGetXbrlErrorMessages from "src/shared/data-access/business/useGetErrorMessages";
import { useAuth } from "src/shared/context/useAuth";

function XbrlErrorMessages() {
  const { t } = useTranslation();
  const { userHomePath } = useAuth();
  const { guid } = useParams();

  if (!guid) {
    return <Navigate to={userHomePath} />;
  }

  const { data, isLoading } = useGetXbrlErrorMessages(guid);

  const columns = useMemo<MRT_ColumnDef<any>[]>(
    () => [
      {
        accessorKey: "level",
        header: t("Level"),
        Cell: ({ row }) => (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "1em",
              justifyContent: "center",
              alignItems: "start",
              padding: "1rem",
              color:
                row.original.level === "warning"
                  ? "darkorange"
                  : row.original.level === "error"
                    ? "red"
                    : "darkblue",
            }}
          >
            {row.original.level}
          </Box>
        ),
      },
      {
        accessorKey: "error_id",
        header: t("Error ID"),
        Cell: ({ row }) => (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "1em",
              justifyContent: "center",
              alignItems: "start",
              padding: "1rem",
              color:
                row.original.level === "warning"
                  ? "darkorange"
                  : row.original.level === "error"
                    ? "red"
                    : "darkblue",
            }}
          >
            {row.original.error_id}
          </Box>
        ),
      },
      {
        accessorKey: "message",
        header: t("Message"),
        Cell: ({ row }) => (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "1em",
              justifyContent: "center",
              alignItems: "start",
              padding: "1rem",
              color:
                row.original.level === "warning"
                  ? "darkorange"
                  : row.original.level === "error"
                    ? "red"
                    : "darkblue",
            }}
          >
            {row.original.message}
          </Box>
        ),
      },
    ],
    [],
  );

  const table = useCustomMaterialTable({
    columns,
    data:
      data?.data?.data?.filter(
        (x: any) =>
          x.error_id !== "ESEF.3.3.1.extensionConceptsNotAnchored" &&
          x.error_id !== "ESEF.4.2.monetaryConceptWithoutBalance" &&
          x.error_id !==
            "ESEF.RTS.Annex.IV.Par.4.2.monetaryConceptWithoutBalance",
      ) ?? [],
    localization: getTableLang(),
    enableTopToolbar: false,
    enableColumnActions: false,
    enableSorting: false,
    manualPagination: false,
    enablePagination: false,
    enableColumnFilters: false,
    enableBottomToolbar: false,
    state: {
      isLoading,
    },
  });
  return (
    <Box
      display="flex"
      justifyContent="center"
      m={1}
      sx={{
        width: "80vw",
        mx: "auto",
        display: "flex",
        flexDirection: "column",
        gap: "1em",
      }}
    >
      {data?.data?.data && data?.data?.data.length > 0 ? (
        <Typography
          sx={{
            backgroundColor: "#d0f4fb",
            padding: "1em",
          }}
        >
          {t("Bρέθηκαν τα εξής")} <span style={{ color: "red" }}>errors </span>/
          <span style={{ color: "darkorange" }}> warnings </span>/
          <span style={{ color: "darkblue" }}> inconsistencies </span>{" "}
          {t("κατά τον έλεγχο των αρχείων.")}
          <br />
          {t("Συμβουλευτικά μπορείτε να ανατρέξετε")}{" "}
          <a
            href="https://www.esma.europa.eu/sites/default/files/library/esma32-60-254_esef_reporting_manual.pdf"
            target="_blank"
            rel="noreferrer"
          >
            {t("εδώ")}
          </a>{" "}
          {t("για πιθανά λάθη.")}
        </Typography>
      ) : (
        <Box m={1}>
          <Typography
            sx={{
              backgroundColor: "#d0f4fb",
              padding: "1em",
            }}
          >
            {t(
              "Δεν βρέθηκαν Errors ή Warnings κατά την επαλήθευση της υποβολής σας",
            )}
          </Typography>
        </Box>
      )}

      {data?.data?.data && data?.data?.data?.length > 0 ? (
        <Box>
          <MaterialReactTable table={table} />
        </Box>
      ) : (
        <></>
      )}
    </Box>
  );
}

export default XbrlErrorMessages;
